import {Box, SxProps} from '@mui/material';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import {isWeaveObjectRef, parseRef} from '../../../../../../react';
import {makeRefObject} from '../../../../../../util/refs';
import {useWeaveflowCurrentRouteContext} from '../../context';
import {DatasetFilePicker} from '../../datasets/CreateDatasetDrawer';
import {
  CREATE_DATASET_ACTIONS,
  CreateDatasetProvider,
  useCreateDatasetContext,
} from '../../datasets/CreateDatasetDrawerContext';
import {
  DatasetEditProvider,
  useDatasetEditContext,
} from '../../datasets/DatasetEditorContext';
import {updateExistingDataset} from '../../datasets/datasetOperations';
import {
  DatasetObjectVal,
  EditableDatasetView,
  EditableDatasetViewProps,
} from '../../datasets/EditableDatasetView';
import {useDatasetSaving} from '../../datasets/useDatasetSaving';
import {useWFHooks} from '../wfReactInterface/context';
import {sanitizeObjectId} from '../wfReactInterface/traceServerDirectClient';
import {FOOTER_HEIGHT_PX} from './constants';
import {useDatasetColumnsContext} from './DatasetColumnsContext';
import {clientBound, hookify} from './hooks';
import {getObjByRef} from './query';

const demoRows = [
  {
    user_input: 'Hello, how are you?',
    expected_output: "I'm good, thank you!",
  },
  {
    user_input: 'What is the capital of France?',
    expected_output: 'Paris',
  },
  {
    user_input: 'What are you?',
    expected_output: 'I am a helpful AI assistant.',
  },
];

const startFromScratchRows = [
  {
    user_input: '',
    expected_output: '',
  },
];

export interface DatasetEditorRef {
  save: () => Promise<string | null>;
  hasChanges: boolean;
  isSaving: boolean;
}

export interface DatasetEditorState {
  hasChanges: boolean;
  isSaving: boolean;
}

export const NewDatasetEditor = forwardRef<
  DatasetEditorRef,
  {
    entity: string;
    project: string;
    useFilePicker?: boolean;
    onSaveComplete?: (datasetRef?: string) => void;
    showNoRowsOverlay?: boolean;
    shouldLoadDemo?: boolean;
    evaluationTitle?: string;
    onChange?: (state: DatasetEditorState) => void;
  }
>(
  (
    {
      entity,
      project,
      useFilePicker = false,
      onSaveComplete,
      showNoRowsOverlay,
      shouldLoadDemo,
      evaluationTitle,
      onChange,
    },
    ref
  ) => {
    const innerRef = useRef<{
      handleSave: () => void;
      hasChanges: boolean;
      isSaving: boolean;
    } | null>(null);
    const saveResolverRef = useRef<
      ((datasetRef: string | null) => void) | null
    >(null);

    const {handleSaveDataset} = useDatasetSaving({
      entity,
      project,
      showToast: false,
      onSaveComplete: datasetRef => {
        onSaveComplete?.(datasetRef);
        // Resolve the promise with the dataset ref
        if (saveResolverRef.current) {
          saveResolverRef.current(datasetRef || null);
          saveResolverRef.current = null;
        }
      },
    });

    // Expose save method via ref
    useImperativeHandle(
      ref,
      () => ({
        save: () => {
          return new Promise<string | null>(resolve => {
            saveResolverRef.current = resolve;
            // Trigger the save on the inner component
            if (innerRef.current) {
              innerRef.current.handleSave();
            } else {
              resolve(null);
            }

            // Set a timeout in case save doesn't complete
            setTimeout(() => {
              if (saveResolverRef.current) {
                saveResolverRef.current = null;
                resolve(null);
              }
            }, 10000); // 10 second timeout
          });
        },
        hasChanges: innerRef.current?.hasChanges ?? false,
        isSaving: innerRef.current?.isSaving ?? false,
      }),
      []
    );

    return (
      <CreateDatasetProvider onPublishDataset={handleSaveDataset}>
        {useFilePicker ? (
          <NewFileDatasetEditorInner
            ref={innerRef}
            evaluationTitle={evaluationTitle}
            onChange={onChange}
          />
        ) : (
          <NewEmptyDatasetEditorInner
            ref={innerRef}
            showNoRowsOverlay={showNoRowsOverlay}
            shouldLoadDemo={shouldLoadDemo}
            evaluationTitle={evaluationTitle}
            onChange={onChange}
          />
        )}
      </CreateDatasetProvider>
    );
  }
);

const NewEmptyDatasetEditorInner = forwardRef<
  {handleSave: () => void; hasChanges: boolean; isSaving: boolean},
  {
    showNoRowsOverlay?: boolean;
    shouldLoadDemo?: boolean;
    evaluationTitle?: string;
    onChange?: (state: DatasetEditorState) => void;
  }
>((props, ref) => {
  const {state, initializeDataset, handlePublishDataset, editorContext} =
    useCreateDatasetContext();
  const {parsedData, isSaving} = state;
  const {editedRows, deletedRows, addedRows} = editorContext;

  // Check if there are any unsaved changes
  const hasChanges = useMemo(() => {
    return editedRows.size > 0 || deletedRows.length > 0 || addedRows.size > 0;
  }, [editedRows.size, deletedRows.length, addedRows.size]);

  // Notify parent of state changes
  const {onChange} = props;
  useEffect(() => {
    onChange?.({
      hasChanges,
      isSaving,
    });
  }, [hasChanges, isSaving, onChange]);

  useEffect(() => {
    if (!parsedData) {
      const initialData = props.shouldLoadDemo
        ? demoRows
        : startFromScratchRows;
      initializeDataset(initialData);
    }
  }, [initializeDataset, parsedData, props.shouldLoadDemo]);

  useImperativeHandle(
    ref,
    () => ({
      handleSave: () => {
        handlePublishDataset();
      },
      hasChanges,
      isSaving,
    }),
    [handlePublishDataset, hasChanges, isSaving]
  );

  if (!parsedData) {
    // When saving, show a loading state instead of null
    if (isSaving) {
      return <WaveLoader size="small" />;
    }
    return null;
  }

  return (
    <EditableDatasetViewInner
      datasetObject={parsedData}
      evaluationTitle={props.evaluationTitle}
      showNoRowsOverlay={props.showNoRowsOverlay}
    />
  );
});

const NewFileDatasetEditorInner = forwardRef<
  {handleSave: () => void; hasChanges: boolean; isSaving: boolean},
  {evaluationTitle?: string; onChange?: (state: DatasetEditorState) => void}
>((props, ref) => {
  const {state, parseFile, handlePublishDataset, editorContext} =
    useCreateDatasetContext();
  const {parsedData, isSaving} = state;
  const {editedRows, deletedRows, addedRows} = editorContext;

  // Check if there are any unsaved changes
  const hasChanges = useMemo(() => {
    return editedRows.size > 0 || deletedRows.length > 0 || addedRows.size > 0;
  }, [editedRows.size, deletedRows.length, addedRows.size]);

  // Notify parent of state changes
  const {onChange} = props;
  useEffect(() => {
    onChange?.({
      hasChanges,
      isSaving,
    });
  }, [hasChanges, isSaving, onChange]);

  useImperativeHandle(
    ref,
    () => ({
      handleSave: () => {
        handlePublishDataset();
      },
      hasChanges,
      isSaving,
    }),
    [handlePublishDataset, hasChanges, isSaving]
  );

  if (!parsedData) {
    return (
      <Box sx={{p: 2}}>
        <DatasetFilePicker handleFileSelect={parseFile} />
      </Box>
    );
  }

  return (
    <EditableDatasetViewInner
      datasetObject={parsedData}
      evaluationTitle={props.evaluationTitle}
    />
  );
});

const editableDatasetViewSx: SxProps = {
  '& .MuiDataGrid-footerContainer': {
    height: `${FOOTER_HEIGHT_PX}px !important`,
    minHeight: `${FOOTER_HEIGHT_PX}px !important`,
  },
};
const EditableDatasetViewInner: React.FC<
  Pick<EditableDatasetViewProps, 'datasetObject'> & {
    evaluationTitle?: string;
    showNoRowsOverlay?: boolean;
  }
> = props => {
  const {state, dispatch, editorContext} = useCreateDatasetContext();
  const {getRowsNoMeta} = editorContext;
  const lastAutoNameRef = useRef<string | null>(null);

  // Get the dataset columns context to update it with current editor state
  const {setColumns} = useDatasetColumnsContext();

  useEffect(() => {
    const rawTitle = props.evaluationTitle?.trim() ?? '';
    const sanitizedBase = rawTitle ? sanitizeObjectId(rawTitle) : '';
    let derivedName = sanitizedBase || 'eval-dataset';
    if (!derivedName.endsWith('-dataset')) {
      derivedName = `${derivedName}-dataset`;
    }

    // Only set automatically if user hasn't overridden: when empty or when we set it last time
    const shouldAutoSet =
      state.datasetName === '' || state.datasetName === lastAutoNameRef.current;
    if (shouldAutoSet && state.datasetName !== derivedName) {
      dispatch({
        type: CREATE_DATASET_ACTIONS.SET_DATASET_NAME,
        payload: derivedName,
      });
      lastAutoNameRef.current = derivedName;
    }
  }, [dispatch, props.evaluationTitle, state.datasetName]);

  // Column propagation handled by EditableDatasetView via onColumnsChange

  // Check if there are any rows (including added rows)
  const hasRows = useMemo(() => {
    return getRowsNoMeta().length > 0;
  }, [getRowsNoMeta]);

  // If no rows and we should show overlay, render it instead of the dataset view
  if (!hasRows && props.showNoRowsOverlay) {
    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          overflow: 'hidden',
        }}>
        <div>No rows available</div>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}>
      <EditableDatasetView
        {...props}
        isEditing={true}
        hideRemoveForAddedRows={false}
        showAddRowButton={true}
        hideIdColumn={true}
        gridSx={editableDatasetViewSx}
        onColumnsChange={cols => {
          setColumns(cols);
        }}
      />
    </Box>
  );
};

const useObjByRef = clientBound(hookify(getObjByRef));

export const ExistingDatasetEditor = forwardRef<
  DatasetEditorRef,
  {
    datasetRef: string;
    onSaveComplete?: (datasetRef?: string) => void;
    onChange?: (state: DatasetEditorState) => void;
  }
>(({datasetRef, onSaveComplete, onChange}, ref) => {
  const innerRef = useRef<{
    handleSave: () => Promise<string | null>;
    hasChanges: boolean;
    isSaving: boolean;
  } | null>(null);
  const datasetObject = useObjByRef(datasetRef);

  useImperativeHandle(
    ref,
    () => ({
      save: async () => {
        if (innerRef.current) {
          return await innerRef.current.handleSave();
        }
        return null;
      },
      hasChanges: innerRef.current?.hasChanges ?? false,
      isSaving: innerRef.current?.isSaving ?? false,
    }),
    []
  );

  if (datasetObject.loading || datasetObject.error || !datasetObject.data) {
    return <WaveLoader size="small" />;
  }
  return (
    <DatasetEditProvider>
      <ExistingDatasetEditorInner
        ref={innerRef}
        datasetRef={datasetRef}
        datasetObject={datasetObject.data.val as DatasetObjectVal}
        onSaveComplete={onSaveComplete}
        onChange={onChange}
      />
    </DatasetEditProvider>
  );
});

const ExistingDatasetEditorInner = forwardRef<
  {
    handleSave: () => Promise<string | null>;
    hasChanges: boolean;
    isSaving: boolean;
  },
  {
    datasetRef: string;
    datasetObject: DatasetObjectVal;
    onSaveComplete?: (datasetRef?: string) => void;
    onChange?: (state: DatasetEditorState) => void;
  }
>(({datasetRef, datasetObject, onSaveComplete, onChange}, ref) => {
  const {
    editedRows,
    deletedRows,
    addedRows,
    resetEditState,
    convertEditsToTableUpdateSpec,
  } = useDatasetEditContext();

  // Get the dataset columns context to update it with current editor state
  const {setColumns} = useDatasetColumnsContext();
  const {useTableUpdate, useObjCreate} = useWFHooks();
  const tableUpdate = useTableUpdate();
  const objCreate = useObjCreate();
  const router = useWeaveflowCurrentRouteContext();
  const [isSaving, setIsSaving] = React.useState(false);

  // Check if there are any unsaved changes
  const hasChanges = React.useMemo(() => {
    return editedRows.size > 0 || deletedRows.length > 0 || addedRows.size > 0;
  }, [editedRows.size, deletedRows.length, addedRows.size]);

  // Notify parent of state changes
  useEffect(() => {
    onChange?.({
      hasChanges,
      isSaving,
    });
  }, [hasChanges, isSaving, onChange]);

  const handleSave = React.useCallback(async () => {
    if (isSaving) return datasetRef;

    setIsSaving(true);
    try {
      // Parse the dataset ref to get entity and project
      const parsedRef = parseRef(datasetRef);
      if (!isWeaveObjectRef(parsedRef)) {
        throw new Error('Invalid dataset reference');
      }

      const entity = parsedRef.entityName;
      const project = parsedRef.projectName;
      const projectId = `${entity}/${project}`;

      // Get the update specs from the context
      const updateSpecs = convertEditsToTableUpdateSpec();

      if (updateSpecs.length === 0) {
        // No changes to save
        return datasetRef;
      }

      // Create the selected dataset object for the update function
      // Only objectId is actually used by updateExistingDataset
      const selectedDataset: any = {
        objectId: parsedRef.artifactName,
      };

      // Update the dataset
      const result = await updateExistingDataset({
        projectId,
        entity,
        project,
        selectedDataset,
        datasetObject,
        updateSpecs,
        tableUpdate,
        objCreate,
        router,
      });

      // Reset the edit state after successful save
      resetEditState();

      // Construct the new ref with the updated digest
      const newRef = makeRefObject(
        entity,
        project,
        'object',
        result.objectId,
        result.objectDigest,
        undefined
      );

      // Call the save complete callback with the new ref
      onSaveComplete?.(newRef);

      return newRef;
    } catch (error: any) {
      console.error('Failed to update dataset:', error);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [
    isSaving,
    datasetRef,
    convertEditsToTableUpdateSpec,
    datasetObject,
    tableUpdate,
    objCreate,
    router,
    resetEditState,
    onSaveComplete,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      handleSave,
      hasChanges,
      isSaving,
    }),
    [handleSave, hasChanges, isSaving]
  );

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}>
      <EditableDatasetView
        datasetObject={datasetObject}
        isEditing={true}
        hideRemoveForAddedRows={false}
        showAddRowButton={true}
        hideIdColumn={true}
        gridSx={editableDatasetViewSx}
        onColumnsChange={cols => {
          setColumns(cols);
        }}
      />
    </Box>
  );
});
